import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import { Button } from 'antd';

import { desktop, tablet } from 'services/ui';
import { IResumeFields, Languages } from 'context';
import { EducationBlock } from 'organisms/EducationBlock';
import { ExperienceBlock } from 'organisms/ExperienceBlock';
import { LanguageBlock } from 'organisms/LanguageBlock';
import { ProfileBasicsBlock } from 'organisms/ProfileBasicsBlock';
import { ProfileCertificatesBlock } from 'organisms/ProfileCertificatesBlock';
import { ProfileProjectsBlock } from 'organisms/ProfileProjectsBlock';
import { ProfileSkillsBlock } from 'organisms/ProfileSkillsBlock';
import type { ICareerProfileFormValues } from './types';

export interface ICareerProfileForm {
  values: ICareerProfileFormValues;
  fields: IResumeFields;
  /** Language for the reused date pickers — English keeps months deterministic. */
  dateLanguage: Languages;
  onChange: (name: string, value: unknown) => void;
  onSave: () => void;
  saving: boolean;
  /** Предпросмотр методиста (ТЗ 0039 доводка): сохранять нечего и некуда. */
  readOnly?: boolean;
}

/**
 * The language-neutral profile editor (spec 0018, increment B). Reuses the
 * Experience / Education / Language blocks from the old resume, repointed at the
 * new profile model; skills, projects and certificates are new.
 */
export const CareerProfileForm = memo(
  ({
    values,
    fields,
    dateLanguage,
    onChange,
    onSave,
    saving,
    readOnly = false,
  }: ICareerProfileForm) => {
    const { t } = useTranslation();

    return (
      <Wrapper>
        {/* 🔴 Один `fieldset disabled` вместо семи пропов `readOnly`: браузер
            гасит ВСЕ поля внутри сам, и забыть новый блок физически нельзя.
            Раньше пряталась только кнопка «Сохранить», а форма оставалась
            живой — плашка обещала «изменения не сохраняются», интерфейс
            приглашал их вносить. */}
        <Fields disabled={readOnly}>
          <ProfileBasicsBlock values={values} onChange={onChange} />

          <ProfileSkillsBlock
            skills={values.skills}
            customSkills={values.customSkills}
            onChangeSkills={(skills) => onChange('skills', skills)}
            onChangeCustomSkills={(customSkills) => onChange('customSkills', customSkills)}
          />

          <ProfileProjectsBlock
            projects={values.projects}
            onChange={(projects) => onChange('projects', projects)}
          />

          <ExperienceBlock
            language={dateLanguage}
            fields={fields}
            values={values.experience}
            onExperienceChange={onChange}
            onInputChange={onChange}
          />

          <EducationBlock
            language={dateLanguage}
            fields={fields}
            values={values.education}
            onChangeEducation={onChange}
            onInputChange={onChange}
          />

          <LanguageBlock
            fields={fields}
            languagesList={values.languages}
            onChangeLanguages={onChange}
            onInputChange={onChange}
          />

          <ProfileCertificatesBlock
            certificates={values.certificates}
            onChange={(certificates) => onChange('certificates', certificates)}
          />
        </Fields>

        <SaveRow>
          {readOnly ? null : (
            <Button type="primary" size="large" loading={saving} onClick={onSave}>
              {t('careerProfile.save')}
            </Button>
          )}
        </SaveRow>
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

/* Собственных рамок и отступов у fieldset быть не должно — он здесь
   исключительно ради нативного `disabled`, а не ради вида. */
const Fields = styled.fieldset`
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
`;

const SaveRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 24px 0;

  ${tablet} {
    padding: 12px 0;
  }

  ${desktop} {
    padding: 16px 0;
  }
`;
